import type { Game } from "@/types";
import { getTeamById, getTeamStats } from "@/utils/nba";

interface StatsCompareProps {
  game: Game;
}

export default function StatsCompare({ game }: StatsCompareProps) {
  const home = getTeamById(game.homeTeamId);
  const away = getTeamById(game.awayTeamId);
  if (!home || !away) return null;

  const homeStats = getTeamStats(game, "home");
  const awayStats = getTeamStats(game, "away");

  const rows: { label: string; home: number; away: number; suffix?: string; lowerBetter?: boolean }[] = [
    { label: "篮板", home: homeStats.rebounds, away: awayStats.rebounds },
    { label: "助攻", home: homeStats.assists, away: awayStats.assists },
    { label: "抢断", home: homeStats.steals, away: awayStats.steals },
    { label: "盖帽", home: homeStats.blocks, away: awayStats.blocks },
    { label: "失误", home: homeStats.turnovers, away: awayStats.turnovers, lowerBetter: true },
    { label: "投篮命中率", home: homeStats.fg, away: awayStats.fg, suffix: "%" },
    { label: "三分命中率", home: homeStats.three, away: awayStats.three, suffix: "%" },
    { label: "罚球命中率", home: homeStats.ft, away: awayStats.ft, suffix: "%" },
  ];

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-5 grid grid-cols-3 items-center text-sm">
        <span className="text-left font-medium text-foreground-700">{away.fullName}</span>
        <span className="text-center text-xs text-foreground-400">技术统计对比</span>
        <span className="text-right font-medium text-foreground-700">{home.fullName}</span>
      </div>

      <div className="space-y-4">
        {rows.map((row) => {
          const total = row.home + row.away || 1;
          const homePercent = (row.home / total) * 100;
          const better = row.lowerBetter ? row.home < row.away : row.home > row.away;
          const awayBetter = row.lowerBetter ? row.away < row.home : row.away > row.home;
          return (
            <div key={row.label}>
              <div className="mb-1.5 flex items-center justify-between text-sm">
                <span className={`font-heading font-semibold ${awayBetter ? "text-accent-600" : "text-foreground-700"}`}>
                  {row.away}{row.suffix ?? ""}
                </span>
                <span className="text-xs text-foreground-500">{row.label}</span>
                <span className={`font-heading font-semibold ${better ? "text-accent-600" : "text-foreground-700"}`}>
                  {row.home}{row.suffix ?? ""}
                </span>
              </div>
              <div className="flex h-2 overflow-hidden rounded-full bg-background-200">
                <div className="h-full" style={{ width: `${100 - homePercent}%`, backgroundColor: away.color }} />
                <div className="h-full" style={{ width: `${homePercent}%`, backgroundColor: home.color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}